import { COLLAPSE_DASHBOARD_RETURNS_COOKIE_KEY, EXPAND_NAVIGATION_COOKIE_KEY } from '@/constants/cookie'
import { setCookieValue } from '@/utils/cookie/cookie'
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'

interface LayoutContextType {
  isNavbarPinned: boolean
  isNavbarPreviewed: boolean
  isNavbarExpanded: boolean
  isDashboardReturnsCollapsed: boolean
  setNavbarPinned: (pinned: boolean) => void
  setNavbarPointerPreviewed: (previewed: boolean) => void
  setNavbarExternalFocusPreviewed: (previewed: boolean) => void
  handleDashboardReturnsToggle: (collapsed: boolean) => void
}

const LayoutContext = createContext<LayoutContextType>({
  isNavbarPinned: false,
  isNavbarPreviewed: false,
  isNavbarExpanded: false,
  isDashboardReturnsCollapsed: false,
  setNavbarPinned: () => {},
  setNavbarPointerPreviewed: () => {},
  setNavbarExternalFocusPreviewed: () => {},
  handleDashboardReturnsToggle: () => {},
})

interface Props {
  isNavbarPinnedInitially: LayoutContextType['isNavbarPinned']
  isDashboardReturnsCollapsedInitially: LayoutContextType['isDashboardReturnsCollapsed']
  children: ReactNode
}

export const LayoutProvider = ({ isNavbarPinnedInitially, isDashboardReturnsCollapsedInitially, children }: Props) => {
  const [isNavbarPinned, setNavbarPinned] = useState<boolean>(isNavbarPinnedInitially)
  const [isNavbarPointerPreviewed, setNavbarPointerPreviewed] = useState<boolean>(false)
  const [isNavbarExternalFocusPreviewed, setNavbarExternalFocusPreviewed] = useState<boolean>(false)
  const [isDashboardReturnsCollapsed, setDashboardReturnsCollapsed] = useState<boolean>(
    isDashboardReturnsCollapsedInitially
  )
  const isNavbarPreviewed = isNavbarPointerPreviewed || isNavbarExternalFocusPreviewed

  useEffect(() => {
    setCookieValue(EXPAND_NAVIGATION_COOKIE_KEY, isNavbarPinned)
  }, [isNavbarPinned])

  useEffect(() => {
    setCookieValue(COLLAPSE_DASHBOARD_RETURNS_COOKIE_KEY, isDashboardReturnsCollapsed)
  }, [isDashboardReturnsCollapsed])

  const updateNavbarPinned = useCallback((pinned: boolean) => {
    setNavbarPinned(pinned)
  }, [])

  const updateNavbarPointerPreviewed = useCallback((previewed: boolean) => {
    setNavbarPointerPreviewed(previewed)
  }, [])

  const updateNavbarExternalFocusPreviewed = useCallback((previewed: boolean) => {
    setNavbarExternalFocusPreviewed(previewed)
  }, [])

  const handleDashboardReturnsToggle = useCallback((collapsed: boolean) => {
    setDashboardReturnsCollapsed(collapsed)
  }, [])

  return (
    <LayoutContext.Provider
      value={{
        isNavbarPinned,
        isNavbarPreviewed,
        isNavbarExpanded: isNavbarPinned || isNavbarPreviewed,
        isDashboardReturnsCollapsed,
        setNavbarPinned: updateNavbarPinned,
        setNavbarPointerPreviewed: updateNavbarPointerPreviewed,
        setNavbarExternalFocusPreviewed: updateNavbarExternalFocusPreviewed,
        handleDashboardReturnsToggle,
      }}
    >
      {children}
    </LayoutContext.Provider>
  )
}

export const useLayoutContext = () => {
  const context = useContext(LayoutContext)
  if (!context) {
    throw new Error('useLayoutContext must be used within a LayoutProvider')
  }
  return context
}
